// The in-house stencil set (TODO §21/§7.3) — the deck's own visual vocabulary.
//
// Each stencil is an **element skeleton**: the declarative shape `convertToExcalidrawElements`
// takes, which fills in ids, seeds, versions and fractional indices for us. Authored once,
// with §9.4's Architect defaults baked in, so a dropped shape already looks like the deck
// rather than like a sketch.
//
// **What this is not, yet.** §7.2 wants the palette to BE an Excalidraw library
// (`.excalidrawlib`), which would make three things one thing: these built-ins, the user's
// own saved items (§7.4) and imported public libraries. That is the right end state and the
// public API for it is all there (`updateLibrary`, `mergeLibraryItems`, `restoreLibraryItems`,
// `loadLibraryFromBlob`, `serializeLibraryAsJSON`). It is not here because §17's own ordering
// note applies to it too — the shelf is *content*, and §17 of `diagrams.todo.md` puts
// §7.2–§7.4 last on purpose, "most worth doing with a real deck in front of you". Skeletons
// are the smaller half that makes the palette real today; the library format slots in
// underneath without changing anything above.

// Mirrors `ExcalidrawElementSkeleton` loosely. Typed here rather than imported so this file
// stays outside the lazy chunk (§10.3) — a `import type` would be erased, but the skeleton
// type drags in the whole element type graph and it is not worth the coupling for a shape
// this simple.
export interface StencilSkeleton {
  type: string;
  x: number;
  y: number;
  width?: number;
  height?: number;
  label?: { text: string };
  [key: string]: unknown;
}

export interface Stencil {
  id: string;
  name: string;
  // Search terms beyond the name, so "process" finds the box and "person" finds the actor.
  keywords: string[];
  // Nominal size, used to place the drop at the pointer's CENTRE rather than its top-left —
  // dropping a shape and having it appear down-right of the cursor feels broken.
  width: number;
  height: number;
  // The active tool this stencil corresponds to, when a plain click should mean
  // draw-to-place rather than drop-at-centre (§7.5). Absent = drop only.
  tool?: "rectangle" | "ellipse" | "diamond" | "arrow" | "line" | "text";
  build: (x: number, y: number) => StencilSkeleton[];
}

// §9.4 Architect: no sketchiness, sharp corners. Applied per element rather than relied on
// from `appState`, because a stencil dropped into a scene whose defaults were changed by hand
// should still look like the deck's stencil.
const ARCHITECT = {
  roughness: 0,
  strokeWidth: 2,
  fillStyle: "solid" as const,
};

function box(
  x: number,
  y: number,
  width: number,
  height: number,
  label: string,
  extra: Record<string, unknown> = {},
): StencilSkeleton {
  return {
    type: "rectangle",
    x,
    y,
    width,
    height,
    ...ARCHITECT,
    ...extra,
    label: { text: label },
  };
}

export const STENCILS: Stencil[] = [
  {
    id: "box",
    name: "Box",
    keywords: ["rectangle", "process", "step", "block"],
    width: 180,
    height: 90,
    tool: "rectangle",
    build: (x, y) => [box(x, y, 180, 90, "")],
  },
  {
    id: "rounded-box",
    name: "Rounded box",
    keywords: ["pill", "start", "end", "terminator"],
    width: 180,
    height: 90,
    build: (x, y) => [box(x, y, 180, 90, "", { roundness: { type: 3 } })],
  },
  {
    id: "decision",
    name: "Decision",
    keywords: ["diamond", "choice", "if", "gateway"],
    width: 140,
    height: 140,
    tool: "diamond",
    build: (x, y) => [
      {
        type: "diamond",
        x,
        y,
        width: 140,
        height: 140,
        ...ARCHITECT,
        label: { text: "" },
      },
    ],
  },
  {
    id: "cylinder",
    name: "Cylinder",
    keywords: ["database", "store", "data", "db"],
    width: 140,
    height: 160,
    // Excalidraw has no cylinder primitive, so it is an ellipse cap over a rectangle — the
    // same way the shape is drawn by hand. Grouped by proximity rather than by a groupId:
    // groups are the author's to make, and a stencil that arrives pre-grouped cannot be
    // edited part by part without an ungroup nobody asked for.
    build: (x, y) => [
      {
        type: "rectangle",
        x,
        y: y + 20,
        width: 140,
        height: 120,
        ...ARCHITECT,
      },
      { type: "ellipse", x, y, width: 140, height: 40, ...ARCHITECT },
    ],
  },
  {
    id: "actor",
    name: "Actor",
    keywords: ["person", "user", "role", "stakeholder"],
    width: 60,
    height: 110,
    build: (x, y) => [
      { type: "ellipse", x: x + 15, y, width: 30, height: 30, ...ARCHITECT },
      {
        type: "line",
        x: x + 30,
        y: y + 32,
        width: 0,
        height: 44,
        ...ARCHITECT,
      },
      {
        type: "line",
        x: x + 2,
        y: y + 46,
        width: 56,
        height: 0,
        ...ARCHITECT,
      },
    ],
  },
  {
    id: "chevron",
    name: "Chevron",
    keywords: ["phase", "stage", "step", "timeline", "arrow"],
    width: 200,
    height: 70,
    build: (x, y) => [
      {
        type: "line",
        x,
        y,
        width: 200,
        height: 70,
        ...ARCHITECT,
        points: [
          [0, 0],
          [165, 0],
          [200, 35],
          [165, 70],
          [0, 70],
          [35, 35],
          [0, 0],
        ],
      },
    ],
  },
  {
    id: "callout",
    name: "Callout",
    keywords: ["note", "comment", "annotation", "aside"],
    width: 200,
    height: 100,
    build: (x, y) => [
      box(x, y, 200, 100, "", {
        roundness: { type: 3 },
        backgroundColor: "#fff3bf",
      }),
    ],
  },
  {
    id: "swimlane",
    name: "Swimlane",
    keywords: ["lane", "row", "band", "responsibility"],
    width: 520,
    height: 120,
    build: (x, y) => [
      { type: "rectangle", x, y, width: 520, height: 120, ...ARCHITECT },
      {
        type: "line",
        x: x + 140,
        y,
        width: 0,
        height: 120,
        ...ARCHITECT,
      },
    ],
  },
  {
    id: "arrow",
    name: "Arrow",
    keywords: ["connector", "flow", "link", "to"],
    width: 160,
    height: 0,
    tool: "arrow",
    build: (x, y) => [
      {
        type: "arrow",
        x,
        y,
        width: 160,
        height: 0,
        ...ARCHITECT,
        points: [
          [0, 0],
          [160, 0],
        ],
      },
    ],
  },
  {
    id: "bracket",
    name: "Bracket",
    keywords: ["group", "span", "range", "brace"],
    width: 40,
    height: 180,
    build: (x, y) => [
      {
        type: "line",
        x,
        y,
        width: 40,
        height: 180,
        ...ARCHITECT,
        points: [
          [0, 0],
          [40, 0],
          [40, 180],
          [0, 180],
        ],
      },
    ],
  },
  {
    id: "text",
    name: "Text",
    keywords: ["label", "caption", "title", "words"],
    width: 160,
    height: 32,
    tool: "text",
    build: (x, y) => [{ type: "text", x, y, text: "Text" } as StencilSkeleton],
  },
];

// §7.6 A useful stencil set is immediately too long to scroll, so the palette carries a
// search box. A substring match over name + keywords is enough — the package depends on
// `fuzzy` for its own search, but importing it here would drag the lazy chunk into the
// palette, which lives in the always-loaded left panel.
export function filterStencils(query: string): Stencil[] {
  const q = query.trim().toLowerCase();
  if (!q) return STENCILS;
  return STENCILS.filter(
    (s) =>
      s.name.toLowerCase().includes(q) || s.keywords.some((k) => k.includes(q)),
  );
}
